/**
 * Card video — lazy source attachment and playback for the looping video a
 * product card shows as its second slide (see snippets/aromely-card-video.liquid).
 *
 * Playback needs BOTH conditions to hold:
 *   1. the card is on screen  (IntersectionObserver)
 *   2. the video's slide is the active one  (hover, swipe, carousel advance)
 *
 * Condition 2 matters because the video sits behind the hero image at rest. A
 * fade carousel stacks every slide in the same box, so intersection alone would
 * report a hidden slide as visible and we would burn bandwidth on a video
 * nobody can see.
 *
 * The two carousels in the theme mark the active slide differently:
 *   - slyde (product-card)      → `.slyde-slide-active` on the slide element
 *   - slideshow (card-gallery)  → `aria-hidden="false"` on <slideshow-slide>
 * Both are attribute mutations, so one MutationObserver per slide covers them.
 *
 * `loading="lazy"` is ignored on <video>, so deferral is done here: the markup
 * ships `preload="none"` and a `data-src`, and the real `src` is attached only
 * when the video is first about to play.
 */

const SELECTOR = '[data-aromely-card-video]';
const SLIDE_SELECTOR = '.slyde-slide, slideshow-slide';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** @type {IntersectionObserver | null} */
let cardObserver = null;

/** @type {WeakSet<HTMLVideoElement>} */
const wired = new WeakSet();

/** @type {WeakMap<HTMLVideoElement, boolean>} */
const onScreen = new WeakMap();

/** Shoppers who asked for less motion get the poster and nothing else. */
const prefersReducedMotion = () => window.matchMedia?.(REDUCED_MOTION).matches ?? false;

/**
 * @param {HTMLVideoElement} video
 * @returns {Element | null}
 */
const slideOf = (video) => video.closest(SLIDE_SELECTOR);

/**
 * @param {Element | null} slide
 * @returns {boolean}
 */
const isSlideActive = (slide) => {
  if (!slide) return true; // Not in a carousel — visibility is the only gate.
  if (slide.classList.contains('slyde-slide')) return slide.classList.contains('slyde-slide-active');
  const hidden = slide.getAttribute('aria-hidden');
  if (hidden !== null) return hidden === 'false';
  return true;
};

/**
 * @param {HTMLVideoElement} video
 */
const sync = async (video) => {
  const shouldPlay = onScreen.get(video) === true && isSlideActive(slideOf(video));

  if (!shouldPlay) {
    if (!video.paused) video.pause();
    return;
  }

  const src = video.dataset.src;
  if (src && !video.src) video.src = src;

  if (!video.paused) return;
  try {
    await video.play();
  } catch {
    // Autoplay can still be refused (power saving, data saver). The poster is
    // already showing the right frame, so there is nothing to recover.
  }
};

const getCardObserver = () => {
  if (cardObserver) return cardObserver;
  cardObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        onScreen.set(video, entry.isIntersecting);
        sync(video);
      }
    },
    { rootMargin: '200px 0px', threshold: 0.01 }
  );
  return cardObserver;
};

/** @param {ParentNode} [root] */
export const initCardVideos = (root = document) => {
  if (prefersReducedMotion()) return;

  for (const element of root.querySelectorAll(SELECTOR)) {
    const video = /** @type {HTMLVideoElement} */ (element);
    if (wired.has(video)) continue;
    wired.add(video);

    const slide = slideOf(video);
    if (slide) {
      new MutationObserver(() => sync(video)).observe(slide, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden'],
      });
    }

    getCardObserver().observe(video);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCardVideos(), { once: true });
} else {
  initCardVideos();
}

// Grids are replaced wholesale by the inline category filters and by infinite
// scroll, so re-scan when new cards land rather than only at page load.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initCardVideos(event.target);
});
